import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';

type ToastKind = 'success' | 'error' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  action?: ToastAction;
}

interface ToastOptions {
  action?: ToastAction;
  durationMs?: number;
}

interface ToastApi {
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | undefined>(undefined);

const KIND_STYLES: Record<ToastKind, { classes: string; icon: React.ReactNode }> = {
  success: {
    classes: 'bg-white dark:bg-gray-900 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100',
    icon: <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />,
  },
  error: {
    classes: 'bg-white dark:bg-gray-900 border-red-200 dark:border-red-900 text-gray-900 dark:text-gray-100',
    icon: <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0" />,
  },
  info: {
    classes: 'bg-white dark:bg-gray-900 border-blue-200 dark:border-blue-900 text-gray-900 dark:text-gray-100',
    icon: <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />,
  },
};

// Replaces window.alert(): non-blocking messages stacked at the bottom of the screen.
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((kind: ToastKind, message: string, options: ToastOptions = {}) => {
    const id = nextId.current++;
    setToasts((current) => [...current.slice(-3), { id, kind, message, action: options.action }]);
    // Errors stay longer: they usually need reading.
    const duration = options.durationMs ?? (kind === 'error' ? 8000 : options.action ? 7000 : 4000);
    window.setTimeout(() => dismiss(id), duration);
  }, [dismiss]);

  const api = useMemo<ToastApi>(() => ({
    success: (message, options) => show('success', message, options),
    error: (message, options) => show('error', message, options),
    info: (message, options) => show('info', message, options),
  }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-4 z-[100] flex flex-col gap-2 sm:w-96 pointer-events-none" aria-live="polite">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.kind === 'error' ? 'alert' : 'status'}
            className={`toast pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 shadow-xl shadow-gray-900/10 dark:shadow-black/40 ${KIND_STYLES[toast.kind].classes}`}
          >
            {KIND_STYLES[toast.kind].icon}
            <p className="flex-1 text-sm font-medium break-words">{toast.message}</p>
            {toast.action && (
              <button
                onClick={() => {
                  toast.action?.onClick();
                  dismiss(toast.id);
                }}
                className="link text-sm font-semibold shrink-0"
              >
                {toast.action.label}
              </button>
            )}
            <button
              onClick={() => dismiss(toast.id)}
              className="-m-1 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800 shrink-0"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
